import GroupApi from '@api/GroupApi';
import LoginApi from '@api/LoginApi';
import SearchApi from '@api/SearchApi';
import UserApi from '@api/UserApi';
import { NO_RECORD_FOUND } from '@api/Statuses';

export default {
  addUsersToGroup({ commit }, users) {
    commit('SET_USER_LOADING', true);
    let reqs = [];
    users.forEach(user => {
      let userReq = new UserApi().update(user);
      reqs.push(userReq);
    });
    return Promise.all(reqs)
      .then(values => {
        return values;
      })
      .catch(err => {
        console.log(err);
        return [];
      })
      .finally(() => {
        commit('SET_USER_LOADING', false);
      });
  },
  createUser({ commit }, payload) {
    commit('SET_USER_LOADING', true);
    let u = {
      ...payload
    };
    // console.log('createUser:', u);
    return new UserApi()
      .create(u)
      .then(res => {
        // commit('CLEAR_ACTIVE_USER');
        return res;
      })
      .catch(err => {
        return {
          userMessage: 'Error creating user',
          status: 'UserCreateError'
        };
      })
      .finally(() => {
        commit('SET_USER_LOADING', false);
      });
  },
  deactivateUser({ commit }, userId) {
    commit('SET_DEACTIVATING', true);
    return new UserApi()
      .deactivateUser({
        body: {
          userId
        }
      })
      .then(_ => {
        return true;
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        commit('SET_DEACTIVATING', false);
      });
  },
  async fetchAccountTypeOptions({ commit }) {
    commit('SET_USER_LOADING', true);
    try {
      let response = await new UserApi().getAccountTypeOptions();
      let options = Object.keys(response).map(text => {
        return { text, value: response[text] };
      });
      commit('SET_ACCOUNT_TYPE_OPTIONS', options);
    } catch (error) {
      console.log(error);
    } finally {
      commit('SET_USER_LOADING', false);
    }
  },
  async fetchAccountTypeRelations({ commit }) {
    commit('SET_USER_LOADING', true);
    try {
      let response = await new UserApi().getAccountTypeRelations();
      let relations = [];
      if (response.status !== 'NoRecordFound') {
        relations = Object.keys(response).map(key => ({
          value: response[key],
          text: key
        }));
      }
      commit('SET_ACCOUNT_TYPE_RELATIONS', relations);
    } catch (error) {
      console.log(error);
    } finally {
      commit('SET_USER_LOADING', false);
    }
  },
  fetchSubGroupsForGroup({ commit }, groupId) {
    if (!groupId) {
      return;
    }
    commit('SET_USER_LOADING', true);
    return new GroupApi()
      .search({
        id: groupId,
        name: '',
        isParentGroupSearch: false
      })
      .then(res => {
        if (!res.groups) {
          throw new Error(`No groups found for groupId: ${groupId}`);
        }
        let groupInfo = null;
        groupInfo = res.groups.find(g => g.groupId == groupId);
        if (groupInfo) {
          let subGroupsInGroup = groupInfo.subGroups.map(sg => ({
            ...sg,
            groupTerminationDate:
              sg.groupTerminationDate || groupInfo.terminationDate,
            groupEffectiveDate: sg.groupEffectiveDate || groupInfo.effectiveDate
          }));
          return { subGroupsInGroup, groupInfo };
        } else {
          console.warn(`Unable to find group ${groupId} in payload`);
        }
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        commit('SET_USER_LOADING', false);
      });
  },
  fetchTransactionHistory({ commit }, userId) {
    commit('SET_HISTORY_LOADING', true);
    return new UserApi()
      .history({
        userId
      })
      .then(history => {
        if (Array.isArray(history) && history.status !== NO_RECORD_FOUND) {
          commit('SET_ACTIVE_USER_HISTORY', history);
        }
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        commit('SET_HISTORY_LOADING', false);
      });
  },
  fetchUserById({ commit }, userId) {
    if (!userId) {
      throw new Error(
        'You need to provide a userId for me to fetch one using it'
      );
    } else {
      commit('SET_USER_LOADING', true);
      return new UserApi()
        .search({
          userId,
          requiredUserProfileOnly: true
        })
        .then(user => {
          commit('SET_ACTIVE_USER', user);
        })
        .catch(ex => {
          console.error('error encountered when fetching user', ex);
        })
        .finally(() => {
          commit('SET_USER_LOADING', false);
        });
    }
  },
  fetchUsersForGroupAdd({ commit }, userIds) {
    let reqs = [];
    userIds.forEach(userId => {
      let userReq = new LoginApi().userRole({ body: { userId: userId } });
      reqs.push(userReq);
    });
    commit('SET_USER_LOADING', true);
    return Promise.all(reqs)
      .then(values => {
        return values;
      })
      .catch(err => {
        console.log(err);
        return [];
      })
      .finally(() => {
        commit('SET_USER_LOADING', false);
      });
  },
  fetchUserRoleForClone({}, userId) {
    if (!userId) {
      throw new Error('You need to provide a userId for me to fetch them');
    } else {
      return new UserApi()
        .userRole({ body: { userId } })
        .then(role => {
          return role;
        })
        .catch(ex => {
          console.error('Unable to fetch user role!');
          return null;
        });
    }
  },
  fetchUserWithRoleById({ commit }, { userId }) {
    if (!userId) {
      throw new Error('You need to provide a userId for me to fetch them');
    } else {
      commit('SET_USER_LOADING', true);
      let commitUser = 'ACTIVE';
      return new UserApi()
        .userRole({
          body: { userId }
        })
        .then(role => {
          // some users have phone and phoneType coming back as the string "null"  This corrects that
          if (role.user.countryCode && role.user.countryCode === 'null') {
            role.user.countryCode = 'US';
          }
          if (role.user.phone && role.user.phone === 'null') {
            role.user.phone = null;
          }
          if (role.user.phoneType && role.user.phoneType === 'null') {
            role.user.phoneType = null;
          }

          if (!role.subGroupAccessPending) {
            role.subGroupAccessPending = [];
          }
          if (!role.subGroupAccess) {
            role.subGroupAccess = [];
          }
          // console.log(`<><><><><><><><><><> role.user`, role.user);
          commit(`SET_${commitUser}_USER`, role.user);
          delete role.user;
          commit(`SET_${commitUser}_USER_ROLE`, role);
        })
        .catch(ex => {
          console.error('error encountered when fetching user', ex);
          // clear the active user when we error
          commit(`CLEAR_${commitUser}_USER`);
        })
        .finally(() => {
          commit('SET_USER_LOADING', false);
        });
    }
  },
  searchGroupsForUserAdd({ commit }, opts) {
    return new SearchApi({
      simulateExample: 'Group Search - Group Name Search'
    })
      .searchGroups(opts)
      .then(res => {
        commit('SET_SEARCH_GROUPS', {
          groups: res.groups,
          parentSearch: opts.isParentGroupSearch,
          isInternal: opts.isInternal
        });
      })
      .catch(err => {
        console.error(err);
        commit('SET_SEARCH_GROUPS', { groups: [], parentSearch: false });
      });
  },
  updateUser({ commit }, user) {
    commit('SET_USER_LOADING', true);
    let u = {
      ...user
    };

    return new UserApi()
      .update(u)
      .then(res => {})
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        commit('SET_USER_LOADING', false);
      });
  },
  updatesmsOptInOut({ commit }, value) {
    return new UserApi()
      .updatesmsOptInOut({
        smsOptInOut: value
      })
      .then(res => {
        console.log('success');
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {});
  }
};
